import React, { useState, useEffect, useRef } from 'react';
import logoAtom from '../../images/logo/logo.png';
import bgAtom from '../../images/logo/bgAtom.png';
import './AtomTestnet.css';

// --- UTILIDADES DE SIMULACIÓN ---
const generateHash = () => '0x' + Array.from({length: 8}, () => Math.floor(Math.random()*16).toString(16)).join('') + '...' + Array.from({length: 4}, () => Math.floor(Math.random()*16).toString(16)).join('');
const generateWallet = () => '0x' + Array.from({length: 4}, () => Math.floor(Math.random()*16).toString(16)).join('');

// Mensajes técnicos para el log
const LOG_MESSAGES = [
  { type: 'INFO', msg: 'Syncing block headers from peer 104.23.xx.xx' },
  { type: 'SUCCESS', msg: 'Block propagation confirmed (4ms)' },
  { type: 'INFO', msg: 'Updating Mempool: +450 pending txs' },
  { type: 'WARN', msg: 'High throughput detected in shard #4' },
  { type: 'INFO', msg: 'Verifying Merkle Root hash integrity...' },
  { type: 'SUCCESS', msg: 'Consensus reached: Validator node active' },
  { type: 'INFO', msg: 'Broadcasting new transaction set to mesh network' },
  { type: 'INFO', msg: 'Garbage collecting orphaned blocks...' },
  { type: 'WARN', msg: 'Gas price fluctuation detected (+0.5%)' },
  { type: 'SUCCESS', msg: 'State trie updated successfully' },
];

interface LogEntry {
  id: number;
  time: string;
  type: string;
  msg: string;
}

interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  status: 'Confirmed' | 'Pending';
  timestamp: number;
}

interface Block {
  number: number;
  validator: string;
  txs: number;
  gasUsed: string;
  timestamp: number;
}

const AtomTestnet: React.FC = () => {
  // --- ESTADO GLOBAL ---
  const [latestBlock, setLatestBlock] = useState(1254890);
  const [txCount, setTxCount] = useState(85321450);
  const [gasPrice, setGasPrice] = useState(0.003);
  
  // Datos Gráficos
  const [tpsData, setTpsData] = useState<number[]>(new Array(40).fill(100)); 
  const [gasUsageHistory, setGasUsageHistory] = useState<number[]>(new Array(20).fill(60));
  const [currentGasUsage, setCurrentGasUsage] = useState(78);

  // Tablas y Logs
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logIdCounter = useRef(0);
  
  // Timer para Bloque (Countdown)
  const [blockProgress, setBlockProgress] = useState(0); // 0 a 100
  const lastBlockTimeRef = useRef(Date.now());
  const BLOCK_TIME_MS = 3000;
  
  // Timer para UI (flash dots etc)
  const [, setTick] = useState(0);

  // --- MOTOR DE SIMULACIÓN ---
  useEffect(() => {
    // 1. Reloj Global (1s)
    const secondInterval = setInterval(() => {
      setTick(t => t + 1);

      // TPS High Performance
      const newTps = Math.random() * 20 + 90; 
      setTpsData(prev => [...prev.slice(1), newTps]);

      setGasPrice(prev => Math.max(0.001, prev + (Math.random() - 0.5) * 0.0005));
      
      setCurrentGasUsage(prev => {
        const next = Math.max(40, Math.min(95, prev + (Math.random() - 0.5) * 15));
        setGasUsageHistory(h => [...h.slice(1), next]);
        return next;
      });
    }, 1000);

    // 2. Generador de Bloques (3s)
    const blockInterval = setInterval(() => {
      setLatestBlock(prev => {
        const newNum = prev + 1;
        const newBlock: Block = {
          number: newNum,
          validator: generateWallet(),
          txs: Math.floor(Math.random() * 5000) + 1000,
          gasUsed: (Math.random() * 5 + 10).toFixed(1) + 'M',
          timestamp: Date.now()
        };
        setBlocks(prevB => [newBlock, ...prevB].slice(0, 50));
        setTxCount(c => c + newBlock.txs);
        
        // Resetear timer visual de bloque
        lastBlockTimeRef.current = Date.now();
        setBlockProgress(0);

        return newNum;
      });
    }, BLOCK_TIME_MS);

    // 2.1 ANIMACIÓN SUAVE DEL CIRCULO (Cada 30ms)
    const progressInterval = setInterval(() => {
        const elapsed = Date.now() - lastBlockTimeRef.current;
        const percent = Math.min(100, (elapsed / BLOCK_TIME_MS) * 100);
        setBlockProgress(percent);
    }, 30);

    // 3. Generador de Txs (300ms)
    const txInterval = setInterval(() => {
      const newTx: Transaction = {
        hash: generateHash(),
        from: generateWallet(),
        to: generateWallet(),
        value: (Math.random() * 50).toFixed(4),
        status: Math.random() > 0.1 ? 'Confirmed' : 'Pending',
        timestamp: Date.now()
      };
      setTransactions(prev => [newTx, ...prev].slice(0, 50));
    }, 300);

    // 4. Generador de LOGS DE TERMINAL (800ms)
    const logInterval = setInterval(() => {
      const randomLog = LOG_MESSAGES[Math.floor(Math.random() * LOG_MESSAGES.length)];
      const now = new Date();
      const timeString = `${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}:${now.getSeconds().toString().padStart(2,'0')}.${now.getMilliseconds().toString().padStart(3,'0')}`;
      
      const newEntry: LogEntry = {
        id: logIdCounter.current++,
        time: timeString,
        type: randomLog.type,
        msg: randomLog.msg
      };

      setLogs(prevLogs => {
        const updated = [newEntry, ...prevLogs];
        return updated.slice(0, 5); 
      });
    }, 800);

    return () => {
      clearInterval(secondInterval);
      clearInterval(blockInterval);
      clearInterval(txInterval);
      clearInterval(logInterval);
      clearInterval(progressInterval);
    };
  }, []);

  const getAge = (timestamp: number) => {
    const diff = Math.floor((Date.now() - timestamp) / 1000);
    if (diff === 0) return 'Just now';
    return `${diff}s ago`;
  };

  // --- RENDER CIRCLE PROGRESS ---
  const renderBlockTimer = () => {
      const radius = 50;
      const circumference = 2 * Math.PI * radius;
      const offset = circumference - (blockProgress / 100) * circumference;
      const secondsLeft = Math.max(0, (3 - (blockProgress / 100) * 3)).toFixed(1);

      return (
        <div className="atom-timer-container">
            <svg className="atom-timer-svg" width="110" height="110">
                {/* Fondo del círculo */}
                <circle cx="55" cy="55" r={radius} stroke="rgba(255,255,255,0.05)" strokeWidth="6" fill="transparent" />
                {/* Progreso */}
                <circle 
                    cx="55" cy="55" r={radius} 
                    stroke="#00c8ff" 
                    strokeWidth="6" 
                    fill="transparent"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 30ms linear' }}
                    transform="rotate(-90 55 55)"
                />
            </svg>
            <div className="atom-timer-text">
                <span className="atom-timer-sec">{secondsLeft}s</span>
                <span className="atom-timer-label">NEXT BLOCK</span>
            </div>
        </div>
      );
  };

  // --- RENDER CHARTS (Igual que antes) ---
  const renderTpsChart = () => {
    const width = 1000;
    const height = 300;
    const minVisual = 80;
    const maxVisual = 120; 
    const range = maxVisual - minVisual;
    const stepX = width / (tpsData.length - 1);

    const points = tpsData.map((val, i) => {
      const x = i * stepX;
      const normalizedVal = Math.max(0, Math.min(1, (val - minVisual) / range));
      const y = height - normalizedVal * height;
      return `${x},${y}`;
    }).join(' ');

    const areaPoints = `${points} ${width},${height} 0,${height}`;

    return (
      <svg className="atom-chart-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="tpsGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#00c8ff" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#00c8ff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1="0" y1={height/2} x2={width} y2={height/2} stroke="rgba(255,255,255,0.05)" strokeDasharray="5" />
        <line x1="0" y1={height/4} x2={width} y2={height/4} stroke="rgba(255,255,255,0.05)" strokeDasharray="5" />
        
        <polygon points={areaPoints} className="atom-chart-area" />
        <polyline points={points} className="atom-chart-line" />
      </svg>
    );
  };

  const renderGasChart = () => {
    const width = 400;
    const height = 300;
    const stepX = width / (gasUsageHistory.length - 1);
    const points = gasUsageHistory.map((val, i) => {
      const x = i * stepX;
      const y = height - (val / 100) * height * 0.8;
      return `${x},${y}`;
    }).join(' ');
    const areaPoints = `${points} ${width},${height} 0,${height}`;

    return (
      <svg className="atom-chart-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="gasGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#2DD4BF" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#2DD4BF" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        <polygon points={areaPoints} className="atom-gas-area" />
        <polyline points={points} className="atom-gas-line" />
      </svg>
    );
  };

  return (
    <section className="atom-dashboard-section">
      
      {/* HEADER */}
      <div className="atom-dash-header">
        <div className="atom-dash-brand">
          <img src={logoAtom} alt="Atom Logo" className="atom-dash-logo" />
          <h1 className="atom-dash-title">Atom Testnet</h1>
        </div>
        
        <div className="atom-live-badge">
          <div className="atom-pulse-dot"></div>
          TESTNET LIVE
        </div>
      </div>

      {/* DASHBOARD GRID */}
      <div className="atom-dash-grid">
        
        {/* ROW 1: MÉTRICAS */}
        <div className="atom-dash-card">
          <div className="atom-card-header">
            Latest Block
          </div>

          <div className="atom-metric-icon">
            <svg viewBox="0 0 24 24">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </div>
          <div className="atom-metric-value val-brand">#{latestBlock.toLocaleString()}</div>
          <div className="atom-metric-sub">Block Height</div>
        </div>

        <div className="atom-dash-card">
          <div className="atom-card-header">Avg Block Time</div>
          <div className="atom-metric-icon">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="7" />
              <line x1="12" y1="7" x2="12" y2="12" />
              <line x1="12" y1="12" x2="16" y2="14" />
              <rect x="10.5" y="3" width="3" height="2" rx="0.5" />
            </svg>
          </div>
          <div className="atom-metric-value val-white">4.2<span style={{fontSize:'1rem', color:'#6B7280'}}>s</span></div>
          <div className="atom-metric-sub">Finality</div>
        </div>

        <div className="atom-dash-card">
          <div className="atom-card-header">Total Transactions</div>
          <div className="atom-metric-icon">
            <svg viewBox="0 0 24 24">
              <polyline points="3 17 8 11 13 14 21 6" />
              <circle cx="8" cy="11" r="1.3" />
              <circle cx="13" cy="14" r="1.3" />
              <circle cx="21" cy="6" r="1.3" />
            </svg>
          </div>
          <div className="atom-metric-value val-white">{(txCount / 1000000).toFixed(2)}<span style={{fontSize:'1rem', color:'#6B7280'}}>M</span></div>
          <div className="atom-metric-sub">Lifetime</div>
        </div>

        <div className="atom-dash-card">
          <div className="atom-card-header">Gas Price</div>
          <div className="atom-metric-icon">
            <svg viewBox="0 0 24 24">
              <rect x="4" y="4" width="7" height="16" rx="1" />
              <polyline points="11 8 15 8 17 10 17 16 14 16" />
              <circle cx="8" cy="14" r="2" />
              <line x1="8" y1="6" x2="9.5" y2="6" />
            </svg>
          </div>
          <div className="atom-metric-value val-white">{gasPrice.toFixed(4)}<span style={{fontSize:'1rem', color:'#6B7280'}}> Gwei</span></div>
          <div className="atom-metric-sub">Ultra Low Cost</div>
        </div>

        {/* ROW 2: GRÁFICOS */}
        <div className="atom-dash-card atom-chart-card">
          <img src={bgAtom} className="atom-chart-watermark" alt="" />
          <div className="atom-card-header" style={{position:'absolute', top:'1.5rem', left:'1.5rem', zIndex:10}}>
            Transactions Per Second (TPS)
          </div>
          <div style={{position:'absolute', top:'1.5rem', right:'1.5rem', zIndex:10, color:'#00c8ff', fontFamily:'Roboto Mono', fontWeight:'bold'}}>
            {tpsData[tpsData.length-1].toFixed(2)}k TPS
          </div>
          {renderTpsChart()}
        </div>

        <div className="atom-dash-card atom-gas-card">
          <div className="atom-card-header" style={{position:'absolute', top:'1.5rem', left:'1.5rem', zIndex:10}}>
            Network Gas Usage
          </div>
          <div className="atom-gas-value-big">
            {currentGasUsage.toFixed(0)}%
          </div>
          <div className="atom-gas-label-big">Block Capacity</div>
          {renderGasChart()}
        </div>

        {/* ROW 3: DIVISIÓN LOGS + TIMER */}
        
        {/* IZQUIERDA: LOGS (Span 3) */}
        <div className="atom-dash-card atom-terminal-card">
          <div className="atom-card-header" style={{marginBottom:'0.5rem'}}>
            <span>System Node Logs</span>
            <span style={{fontSize:'0.7rem', color:'#00c8ff', opacity:0.8}}>LIVE FEED ●</span>
          </div>
          <div className="atom-terminal-content">
            {logs.map(log => (
              <div key={log.id} className="atom-log-line">
                <span className="atom-log-time">[{log.time}]</span>
                <span className={`atom-log-type type-${log.type}`}>{log.type}</span>
                <span className="atom-log-msg">{log.msg}</span>
              </div>
            ))}
          </div>
        </div>

        {/* DERECHA: TIMER (Span 1) - NUEVO */}
        <div className="atom-dash-card atom-timer-card">
            {renderBlockTimer()}
        </div>


        {/* ROW 4: TABLAS DE DATOS */}
        <div className="atom-dash-card atom-logs-card">
          <div className="atom-card-header-logs">Recent Transactions</div>
          <div className="atom-table-container">
            <table className="atom-table">
              <thead>
                <tr>
                  <th>Tx Hash</th>
                  <th>From / To</th>
                  <th>Value</th>
                  <th>Status</th>
                  <th>Age</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.hash} className="atom-row-flash">
                    <td><span className="atom-hash">{tx.hash.substring(0, 14)}...</span></td>
                    <td>{tx.from} <span style={{color:'#6B7280'}}>→</span> {tx.to}</td>
                    <td>{tx.value} ATOM</td>
                    <td>
                      {tx.status === 'Confirmed' ? (
                        <span style={{color:'#10B981', display:'flex', alignItems:'center'}}><span className="atom-status-icon">✅</span>Confirmed</span>
                      ) : (
                        <span style={{color:'#F59E0B', display:'flex', alignItems:'center'}}><span className="atom-status-icon">⏳</span>Pending</span>
                      )}
                    </td>
                    <td className="atom-age-col">{getAge(tx.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="atom-dash-card atom-logs-card">
          <div className="atom-card-header-logs">Validated Blocks</div>
          <div className="atom-table-container">
            <table className="atom-table">
              <thead>
                <tr>
                  <th>Block #</th>
                  <th>Validator</th>
                  <th>Txs</th>
                  <th>Gas Used</th>
                  <th>Age</th>
                </tr>
              </thead>
              <tbody>
                {blocks.map((block) => (
                  <tr key={block.number} className="atom-row-flash">
                    <td style={{color:'#00c8ff', fontWeight:'bold'}}>{block.number}</td>
                    <td>{block.validator}</td>
                    <td>{block.txs}</td>
                    <td>{block.gasUsed}</td>
                    <td className="atom-age-col">{getAge(block.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </section>
  );
};


export default AtomTestnet;